import { createSlice } from "@reduxjs/toolkit";
import { updateDatabase } from "../../api/Setters/update.meta";
import notify from "../../utils/Notification/notify";

const initialState = {
  /**
   * PDF configuration settings used for generating operation-specific PDFs.
   *
   * @property {Object[]} pdfSettings
   * @property {string} pdfSettings[].id
   * @property {string} pdfSettings[].name
   * @property {Object|null} pdfSettings[].triggerTag - Tag that triggers PDF generation on 0→1 edge
   * @property {Object|null} pdfSettings[].plc - PLC metadata / selection for the trigger
   * @property {Object|null} pdfSettings[].resetTag - Tag that resets/disables further PDF generation
   * @property {Object|null} pdfSettings[].startTsTag - Tag extracted from row with unit="startTs"
   * @property {Object|null} pdfSettings[].endTsTag - Tag extracted from row with unit="endTs"
   * @property {string|null} pdfSettings[].triggerTimestamp - HH:mm UTC time trigger
   * @property {("enabled"|"disabled")} pdfSettings[].triggerTimestampState
   * @property {string} pdfSettings[].prologue - Text shown before PDF content
   * @property {string} pdfSettings[].epilogue - Text shown after PDF content
   * @property {string[]} pdfSettings[].emails - List of email recipients
   * @property {string[]} pdfSettings[].barChart - List of email recipients
   * @property {string[]} pdfSettings[].pieChart - List of email recipients
   * @property {Object[]} pdfSettings[].rows
   * @property {Object|null} pdfSettings[].rows[].tag
   * @property {string} pdfSettings[].rows[].unit - "startTs", "endTs", or standard unit
   * @property {boolean} pdfSettings[].rows[].hasAverage
   * @property {boolean} pdfSettings[].rows[].hide
   * @property {boolean} pdfSettings[].rows[].displayBar
   * @property {boolean} pdfSettings[].rows[].displayPie
   * @property {boolean} pdfSettings[].rows[].isBold
   * @property {string} pdfSettings[].rows[].name
   * @property {string} pdfSettings[].rows[].email (deprecated, use pdfSettings[].emails)
   * @property {string} pdfSettings[].lastModifiedTs
   * @property {string} pdfSettings[].operationStatus
   * @property {Object[]} pdfSettings[].lastGenerationTs
   */
  pdfSettings: [],
};
const operationPdfSlice = createSlice({
  name: "operationPdf",
  initialState,
  reducers: {
    setOperationPdfs(state, action) {
      const incoming = action.payload;
      if (Array.isArray(incoming)) state.pdfSettings = incoming;
      else if (
        incoming &&
        typeof incoming === "object" &&
        Array.isArray(incoming.operationPdfs)
      )
        state.pdfSettings = incoming.operationPdfs;
      else state.pdfSettings = [];
    },
    updatePdf(state, action) {
      if (!Array.isArray(state.pdfSettings)) state.pdfSettings = [];
      const updatedPdf = action.payload;
      const now = new Date().toISOString();
      switch (updatedPdf.action) {
        case "add":
          state.pdfSettings.push({ ...updatedPdf.pdf, lastModifiedTs: now });
          break;
        case "update":
          const index = state.pdfSettings.findIndex(
            (p) => p.id === updatedPdf.pdf.id
          );
          if (index !== -1)
            state.pdfSettings[index] = {
              ...updatedPdf.pdf,
              lastModifiedTs: now,
            };
          break;
        case "remove":
          state.pdfSettings = state.pdfSettings.filter(
            (p) => p.id !== updatedPdf.pdf.id
          );
          break;
      }
    },
  },
});

export const { setOperationPdfs, updatePdf } = operationPdfSlice.actions;

// Selector: always returns an array of PDFs
export const selectOperationPdfs = (state) =>
  state?.operationPdf?.pdfSettings ?? [];

// THUNK: DISPATCH STATE UPDATE, THEN PERSIST SNAPSHOT (not the Immer draft)
export const updatePdfAndPersist = (payload) => async (dispatch, getState) => {
  dispatch(updatePdf(payload));
  try {
    const state = getState();
    const pdfs = state?.operationPdf?.pdfSettings ?? [];
    await updateDatabase("operationPdf", pdfs);
  } catch (error) {
    notify("Failed to update PDF settings in database", "error");
    console.error("Error updating PDF settings in database:", error);
  }
};

export default operationPdfSlice.reducer;
